import React, { useCallback } from "react";

import { fork } from "@formant/core/common/fork";
import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { IDashboardLayout } from "@formant/core/model/IDashboardLayout";
import { IFilter } from "@formant/core/model/IFilter";
import { IGroup } from "@formant/core/model/IGroup";
import { ITags } from "@formant/core/model/ITags";
import { IView } from "@formant/core/model/IView";
import { LayoutType } from "@formant/core/model/LayoutType";
import { Uuid } from "@formant/core/model/Uuid";
import { defaultStoreId } from "@formant/rectangle-sdk";
import { Icon } from "@formant/ui/component/common/Icon";
import { Picker, PickerItem } from "@formant/ui/component/picker";
import { EditorMode, useBaseEditorStore } from "@formant/views-editor";

import { track } from "../../../analytics";
import { useRouter } from "../../../context/RouterContext";
import { useStores } from "../../../context/StoreContext";
import { IObserveStore } from "../../../store/IObserveStore";
import { IViewStore } from "../../../store/IViewStore";
import { PopupPortal, usePopup } from "../../../utils/hooks/usePopupHooks";
import { AddActionButton } from "./F3.HomeTabs.styles";

interface IAddViewProps {
    onViewCreated?: (id: Uuid) => void;
    deviceIds: Uuid[];
    tags: ITags;
    group?: IGroup;
}

export const newView = (
    name: string,
    layoutType: LayoutType,
    deviceIds: Uuid[] = [],
    tags: ITags = {},
    groupFilter: IFilter | null = null,
    layout: IDashboardLayout | null = null,
): IView => {
    const emptyFilter = { names: [] };
    const fullFilter = {
        deviceIds: deviceIds || [],
    };

    return {
        name,
        layout: layout || {},
        filter: layoutType === "teleop" ? emptyFilter : fullFilter,
        groupFilter,
        tags: layoutType === "teleop" ? {} : tags,
        layoutType,
        deviceFilter: {},
        index: 0,
        configuration: [],
    };
};

const createView = async (
    view: IView,
    viewStore: IViewStore,
    observeStore: IObserveStore,
): Promise<DefinedEntity<IView>> => {
    const createdView = await viewStore.createSimple(view);
    observeStore.isCustomizing = true;
    useBaseEditorStore(defaultStoreId)?.getState().setMode(EditorMode.Edit);
    observeStore.configuringAnalyticsId = undefined;
    return createdView;
};

export const F3AddView = function AddView(props: IAddViewProps) {
    const { onViewCreated, group } = props;

    const groupFilter: IFilter | null = group?.tagKey
        ? { tags: [{ [group.tagKey]: [group.tagValue] }] }
        : null;

    const {
        isOpen,
        openDropdown,
        closeDropdown,
        setRefElement,
        setTargetElement,
        context,
    } = usePopup<HTMLButtonElement>({
        placement: "bottom-start",
        modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
    });

    const { navigate, routeTo } = useRouter();

    const { organizationStore, viewStore, observeStore } = useStores();
    const navigatorEnabled = false;
    const allowAnalyticsV1View = organizationStore.analyticsV1Enabled();
    const allowDashboardV1View = organizationStore.observabilityV1Enabled();

    const items: PickerItem[] = [
        ...(group || !allowDashboardV1View
            ? []
            : [
                  {
                      tab: "Library",
                      icon: "dashboard" as "dashboard",
                      label: "Dashboard",
                      onClick: useCallback(() => {
                          track("Create Observe View");
                          void navigate(routeTo.createView());
                      }, [navigate, routeTo]),
                  } as PickerItem,
              ]),
        ...(true
            ? [
                  {
                      tab: "Library",
                      icon: "dashboard" as "dashboard",
                      label: "Coherence",
                      onClick: useCallback(async () => {
                          track("Create Observe V2 View");
                          const { deviceIds, tags } = props;
                          const view = newView(
                              "Coherence",
                              "dashboard",
                              deviceIds || [],
                              tags || {},
                              groupFilter,
                          );
                          const { id } = await createView(
                              view,
                              viewStore,
                              observeStore,
                          );
                          onViewCreated?.(id);
                      }, [navigate, routeTo]),
                  },
              ]
            : []),
        ...(groupFilter
            ? []
            : [
                  {
                      tab: "Library",
                      icon: "joystick",
                      label: "Navigator",
                      onClick: useCallback(async () => {
                          track("Create Navigator View");
                          const view = newView("Navigator View", "teleop");
                          const { id } = await createView(
                              view,
                              viewStore,
                              observeStore,
                          );
                          onViewCreated?.(id);
                      }, [navigate, viewStore, observeStore]),
                      disabled: !navigatorEnabled,
                  } as PickerItem,
              ]),
        ...(!allowAnalyticsV1View
            ? []
            : [
                  {
                      tab: "Library",
                      icon: "numeric",
                      label: "Analytics",
                      onClick: useCallback(async () => {
                          track("Create Analytics View");
                          const { deviceIds, tags } = props;
                          const view = newView(
                              "Analytics View",
                              "analytics",
                              deviceIds || [],
                              tags || {},
                              groupFilter,
                          );
                          const { id } = await createView(
                              view,
                              viewStore,
                              observeStore,
                          );
                          onViewCreated?.(id);
                      }, [navigate, viewStore, observeStore]),
                      disabled: !allowAnalyticsV1View,
                  } as PickerItem,
              ]),
    ];

    const goToAddView = useCallback(() => {
        fork(navigate(routeTo.createView(), {}));
    }, []);

    const showDropDown = true;

    return (
        <>
            <AddActionButton
                ref={setRefElement}
                onClick={showDropDown ? openDropdown : goToAddView}
                data-testid="add-view-button"
            >
                <Icon name="plus" />
            </AddActionButton>
            <PopupPortal isOpen={isOpen} context={context}>
                <Picker
                    displayType="small"
                    items={items}
                    ref={setTargetElement}
                    searchPlaceholder="Create a view"
                    style={{ maxHeight: "calc(100vh - 75px)" }}
                    onSelectItem={closeDropdown}
                    onExit={closeDropdown}
                    noSearch={true}
                />
            </PopupPortal>
        </>
    );
};
